export const BRAND = {
  name: "Zaklyn",
  tagline: "Tailored to You.",
  description: "Boutique tailoring concierge for the modern Indian woman.",
  promise: "Perfect fit, doorstep convenience, delivered as soon as tomorrow.",
};

export const HERO = {
  headline: "Zaklyn. Tailored to You.",
  subheadline: "Doorstep measurements. Boutique alterations & stitching. Delivered as soon as tomorrow.",
  ctaPrimary: "Book a Free Measurement",
  ctaSecondary: "Schedule Pickup",
  trustBadges: [
    "24-hour turnaround",
    "Women-focused tailoring",
    "Fit guarantee",
  ],
};

export const CONFIDENCE_STRIP = [
  { icon: "Shield", label: "Hygienic Packaging" },
  { icon: "UserCheck", label: "Verified Stylists" },
  { icon: "Lock", label: "Secure Payments" },
  { icon: "Truck", label: "Doorstep Service" },
];

export const CAROUSEL_SLIDES = [
  {
    headline: "Perfect fit, not compromises.",
    subtext: "Every stitch crafted to your measurements.",
  },
  {
    headline: "From pickup to delivery—handled with care.",
    subtext: "We treat your garments like our own.",
  },
  {
    headline: "Blouses, kurtis, dresses—beautifully stitched & altered.",
    subtext: "Traditional craftsmanship meets modern convenience.",
  },
  {
    headline: "Rework support with fit guarantee.",
    subtext: "Not quite right? We'll make it perfect.",
  },
];

export const HOW_IT_WORKS = {
  title: "How Zaklyn Works",
  subtitle: "Three simple steps to your perfect fit",
  steps: [
    {
      number: "01",
      title: "Book a slot",
      description: "Choose a time that suits you. Our stylist arrives at your doorstep—no salon visits needed.",
      icon: "Calendar",
    },
    {
      number: "02",
      title: "We measure or pick up",
      description: "Professional measurements for new pieces, or we collect your garments for alterations.",
      icon: "Ruler",
    },
    {
      number: "03",
      title: "Tailored & delivered",
      description: "Expert craftsmanship, quality checked, and delivered back to you—as soon as next day.",
      icon: "Package",
    },
  ],
};

export const SERVICES = {
  title: "Our Services",
  subtitle: "Crafted for the modern Indian woman",
  items: [
    {
      title: "Alterations",
      description: "Waist adjustments, length fixes, sleeve modifications, blouse fitting—precision tailoring for your existing wardrobe.",
      features: ["Blouse fitting", "Waist & length", "Sleeve adjustment", "Lining work"],
      cta: "Get started",
    },
    {
      title: "Stitching",
      description: "Custom-made kurtis, blouses, salwar suits, and dresses—crafted to your exact measurements.",
      features: ["Kurtis", "Blouses", "Salwar suits", "Dresses"],
      cta: "Get started",
    },
    {
      title: "Express Service",
      description: "Need it tomorrow? Our express service ensures your garments are ready within 24 hours.",
      features: ["Next-day delivery", "Priority handling", "Quality assured", "Select areas"],
      cta: "Check availability",
    },
  ],
};

export const BEFORE_AFTER = {
  title: "The Difference is in the Drape",
  subtitle: "See how expert tailoring transforms the fit",
  beforeLabel: "Before",
  afterLabel: "After",
};

export const SOCIAL_PROOF = {
  stats: [
    { value: "4.8", suffix: "★", label: "Average rating" },
    { value: "10,000", suffix: "+", label: "Garments perfected" },
    { value: "24h", suffix: "", label: "Express delivery" },
  ],
  testimonialsTitle: "What Our Clients Say",
  testimonials: [
    {
      quote: "Finally, blouses that fit perfectly. Zaklyn understands the Indian woman's body.",
      name: "Priya Sharma",
      location: "Koramangala, Bangalore",
      rating: 5,
    },
    {
      quote: "The doorstep service is a game-changer. No more running to tailors with my busy schedule.",
      name: "Ananya Reddy",
      location: "Indiranagar, Bangalore",
      rating: 5,
    },
    {
      quote: "Got my mother's saree blouse altered—the fit was impeccable. They handled it with such care.",
      name: "Meera Krishnan",
      location: "HSR Layout, Bangalore",
      rating: 5,
    },
    {
      quote: "Express delivery saved my Diwali outfit. Received my kurti in perfect condition, right on time.",
      name: "Deepika Nair",
      location: "Whitefield, Bangalore",
      rating: 5,
    },
    {
      quote: "The quote was transparent, no surprises. Quality work at fair prices.",
      name: "Sneha Iyer",
      location: "JP Nagar, Bangalore",
      rating: 5,
    },
  ],
};

export const GALLERY = {
  title: "The Zaklyn Aesthetic",
  subtitle: "A glimpse into our craft",
};

export const COVERAGE = {
  title: "Service Areas",
  subtitle: "Available in select neighborhoods. Expanding weekly.",
  expandingText: "Don't see your area? We're adding new locations every week.",
  notifyMe: "Notify me when available",
};

export const FAQ = {
  title: "Questions? We've Got Answers",
  items: [
    {
      question: "How does pricing work?",
      answer: "After inspecting your garment, we provide a detailed quote. Prices vary based on the type of work, fabric, and complexity. You approve the quote before we begin—no surprises.",
    },
    {
      question: "What's the turnaround time?",
      answer: "Standard orders are ready in 3-5 days. Express service delivers within 24 hours for select areas. We'll confirm the timeline when you book.",
    },
    {
      question: "What if I'm not satisfied with the fit?",
      answer: "We stand behind our work. If something isn't right, let us know within 48 hours of delivery. We'll pick it up and make it perfect—free of charge.",
    },
    {
      question: "What if I disagree with the quote?",
      answer: "No pressure, ever. If the quote doesn't work for you, we'll return your garment at no cost. You only pay when you approve.",
    },
    {
      question: "How do you handle premium garments?",
      answer: "With utmost care. Silks, chiffons, and embroidered pieces receive special attention. We document the condition at pickup and use appropriate techniques for each fabric type.",
    },
    {
      question: "Is the packaging hygienic?",
      answer: "Absolutely. Every garment is cleaned, pressed, and sealed in fresh packaging before delivery. We follow strict hygiene protocols throughout our process.",
    },
  ],
};

export const FOOTER = {
  tagline: "Made for women who don't settle for average fit.",
  support: "Zaklyn Support",
  links: {
    company: [
      { label: "How it Works", href: "/how-it-works" },
      { label: "Services", href: "/services" },
      { label: "Pricing", href: "/pricing" },
    ],
    legal: [
      { label: "Terms of Service", href: "/terms" },
      { label: "Privacy Policy", href: "/privacy" },
    ],
  },
  contact: {
    whatsapp: "+91 98765 43210",
    email: "hello@zaklyn.com",
  },
  copyright: "Zaklyn. All rights reserved.",
};

export const NAV = {
  links: [
    { label: "How it Works", href: "/how-it-works" },
    { label: "Services", href: "/services" },
    { label: "Pricing", href: "/pricing" },
    { label: "Track Order", href: "/customer/orders" },
  ],
  cta: "Book Measurement",
  login: "Login",
};

export const EMPTY_STATES = {
  orders: {
    title: "No orders yet",
    description: "Your tailoring journey begins with a single measurement. Book your first appointment and experience the Zaklyn difference.",
    cta: "Book Your First Measurement",
  },
  appointments: {
    title: "No appointments scheduled",
    description: "Ready to get measured? Choose a time that works for you, and we'll come to you.",
    cta: "Schedule Appointment",
  },
  measurements: {
    title: "No measurements on file",
    description: "Once we take your measurements, they'll appear here. Perfect fit starts with precise measurements.",
    cta: "Book Measurement",
  },
};

export const QUOTE_PAGE = {
  title: "Your Quote",
  subtitle: "Review the details of your tailoring order",
  transparency: "Transparent pricing, no hidden fees",
  expiry: "This quote expires in",
  itemsLabel: "Items & Services",
  totalLabel: "Total",
  approveButton: "Approve & Proceed",
  rejectButton: "Decline Quote",
  questions: "Questions about this quote?",
  contact: "Reach out to our team",
};

export const CHECKOUT = {
  title: "Complete Your Order",
  subtitle: "Secure payment for your tailoring service",
  securityNote: "Your payment information is encrypted and secure",
  payButton: "Pay Now",
  processing: "Processing your payment...",
};

export const TRACKING = {
  title: "Track Your Order",
  subtitle: "Follow your garment's journey",
  statuses: {
    QUOTE_PENDING: { label: "Quote Pending", description: "We're preparing your personalized quote" },
    QUOTE_SENT: { label: "Quote Ready", description: "Your quote is ready for review" },
    QUOTE_APPROVED: { label: "Quote Approved", description: "Thank you! We'll begin work soon" },
    AWAITING_PICKUP: { label: "Pickup Scheduled", description: "We'll collect your garments soon" },
    PICKED_UP: { label: "In Transit", description: "Your garments are on their way to our workshop" },
    AT_WORKSHOP: { label: "At Workshop", description: "Expert tailors are working on your order" },
    IN_PRODUCTION: { label: "In Production", description: "Your garments are being crafted" },
    QC_PASSED: { label: "Quality Check Passed", description: "Looking perfect! Ready for delivery" },
    OUT_FOR_DELIVERY: { label: "Out for Delivery", description: "Your order is on its way to you" },
    DELIVERED: { label: "Delivered", description: "Enjoy your beautifully tailored garments!" },
  },
  needHelp: "Need help with your order?",
};

export const DISPUTE = {
  title: "We're Here to Help",
  subtitle: "Let us know what went wrong and we'll make it right",
  formTitle: "Describe the Issue",
  submitButton: "Submit Request",
  thankYou: "Thank you for your feedback",
  resolution: "Our team will review and respond within 24 hours",
};

export const REWORK = {
  title: "Rework Request",
  subtitle: "Not quite perfect? We'll fix it",
  guarantee: "Our fit guarantee covers adjustments at no extra cost",
  submitButton: "Request Rework",
  processing: "Processing your request...",
  success: "Rework scheduled. We'll pick up your garment soon.",
};

export const HELP_BUTTON = {
  title: "Need Help?",
  options: [
    { label: "WhatsApp Us", icon: "MessageCircle" },
    { label: "View FAQs", icon: "HelpCircle" },
    { label: "Request Callback", icon: "Phone" },
  ],
};

export const BOOKING = {
  title: "Book Your Appointment",
  subtitle: "Choose a time that works for you",
  whatToExpect: {
    title: "What to Expect",
    items: [
      "Our stylist will arrive at your doorstep at the scheduled time",
      "The measurement process takes about 15-20 minutes",
      "Wear fitted clothing for accurate measurements",
      "Have your garments ready if booking for alterations",
    ],
  },
  confirmButton: "Confirm Booking",
  slots: {
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
  },
};
